import styled from '@emotion/styled'
import { useContext } from 'react'
import { StyledHTMLElement } from 'shop/theme/types'
import { LandingContext } from '../LandingContext'
import {
  IoBicycleOutline as DeliveryIcon,
  IoBagHandleOutline as PickupIcon,
  IoTimeOutline as TimeIcon
} from 'react-icons/io5'
import { StoreRankReasons } from '../types'
import { getStoreTagContent } from './utils'
import { useMediaQueries } from 'shop/hooks'

type Props = {
  sortedRankReasons: StoreRankReasons[]
  isDeliveryEnabled: boolean
  isPickupEnabled: boolean
}

export type TagItems = 'DELIVERY' | 'PICKUP' | 'TIME'

const StoreTags = ({
  sortedRankReasons,
  isDeliveryEnabled,
  isPickupEnabled
}: Props) => {
  const { sortCriteria, currentLocation } = useContext(LandingContext)
  const { isMobile } = useMediaQueries()

  /** Split words into an array for Mobile so they can be rendered on separate lines */
  const formatWordsForDisplay = (value: string | null): string[] => {
    if (!value) return []
    if (isMobile) {
      const splitWords = value.split(/\s+/)
      return splitWords
    }
    return [value]
  }

  // Do not render if there are no rank reasons from the Store.
  if (!sortedRankReasons.length) return <></>

  const tagsToRender: TagItems[] = []

  const iconMap = {
    DELIVERY: <DeliveryIcon />,
    PICKUP: <PickupIcon />,
    TIME: <TimeIcon />
  }

  const createTagElement = (tag: TagItems): JSX.Element => {
    const { isEnabled, text } = getStoreTagContent({
      tag,
      isDeliveryEnabled,
      isPickupEnabled,
      hasLocation: !!currentLocation?.address,
      sortedRankReasons,
      sortCriteria
    })
    return (
      <StoreTagItem key={tag} isEnabled={isEnabled}>
        <StoreTagIconContainer>{iconMap[tag]}</StoreTagIconContainer>

        {formatWordsForDisplay(text).map((word, index) => (
          <span key={`${word}-${index}`}>{word}</span>
        ))}
      </StoreTagItem>
    )
  }

  // Append the appropriate TagItems to an array ready for rendering
  if (!!sortCriteria.fulfillmentTypes.length) {
    if (sortCriteria.fulfillmentTypes.includes('DELIVERY'))
      tagsToRender.push('DELIVERY')
    if (sortCriteria.fulfillmentTypes.includes('PICKUP'))
      tagsToRender.push('PICKUP')
  }
  if (!!sortCriteria.fulfillmentDate || !!sortCriteria.fulfillmentTime)
    tagsToRender.push('TIME')

  return (
    <StoreTagsContainer>
      {tagsToRender.map((tag) => {
        return createTagElement(tag)
      })}
    </StoreTagsContainer>
  )
}

const StoreTagsContainer = styled.ul<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  flex: 1
}))
const StoreTagItem = styled.li<StyledHTMLElement & { isEnabled: boolean }>(
  ({ theme, isEnabled }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    width: '33%',
    opacity: isEnabled ? 1 : 0.25,

    '& span': {
      fontSize: '13px',
      textAlign: 'center'
    }
  })
)

const StoreTagIconContainer = styled.div<StyledHTMLElement & {}>(
  ({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '& svg': {
      height: '20px',
      width: '20px'
    }
  })
)

export default StoreTags
